import { Component, ElementRef, Input, NgZone, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MapsAPILoader } from '@agm/core';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

import { MobilePhoneNumberValidator } from '../../../utils/phone-number';
import { AddressField, getAddressField } from '../../../../lib/lib-ngx/utils/AddressField';
import { IHouseDetailsList } from '../../../../lib/lib-shared/models/House';
import { IAddressContent } from '../../../../lib/lib-shared/api/All.Type';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'lm-manage-address-modal',
  templateUrl: './manage-address-modal.component.html',
  styleUrls: ['./manage-address-modal.component.scss']
})
export class ManageAddressModalComponent implements OnInit {
  @Input('userData') userData: { firstName: string, lastName: string, company?: string, phoneNumber: string, house: IHouseDetailsList };
  public houseForm: FormGroup;

  @ViewChild('shippingAddressElementRef') public shippingAddressElementRef: ElementRef;
  public shippingAddress: google.maps.places.PlaceResult = null;

  @ViewChild('invoiceAddressElementRef') public invoiceAddressElementRef: ElementRef;
  public invoiceAddress: google.maps.places.PlaceResult = null;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    private renderer: Renderer2,
  ) {
  }

  ngOnInit() {
    this.buildForm();
  }

  buildForm() {
    this.houseForm = this.fb.group({
      atHome: [false, Validators.required],
      name: ['', [Validators.required, Validators.maxLength(35)]],
      shippingAddress: this.fb.group({
        lastName: ['', Validators.required],
        firstName: ['', Validators.required],
        company: [''],
        address: ['', Validators.required],
        phoneNumber: ['', [Validators.required, MobilePhoneNumberValidator()]],
        pickupId: [''],
      }),
      differentInvoiceAddress: ['', Validators.required],
    });
    this.initGMaps();

    this.houseForm.controls.differentInvoiceAddress.valueChanges
      .pipe(debounceTime(200),
        distinctUntilChanged())
      .subscribe(differentInvoiceAddress => {
        if (differentInvoiceAddress) {
          this.houseForm.addControl('invoiceAddress', this.fb.group({
            lastName: [this.userData.lastName, Validators.required],
            firstName: [this.userData.firstName, Validators.required],
            company: [this.userData.company],
            address: ['', Validators.required],
          }));

          setTimeout(() => {
            const autocompleteInvoice = new google.maps.places.Autocomplete(this.invoiceAddressElementRef.nativeElement, {
              types: ['address'],
            });

            autocompleteInvoice.addListener('place_changed', () => {
              this.ngZone.run(() => {
                this.invoiceAddress = autocompleteInvoice.getPlace();
              });
            });
            this.renderer.listen(this.invoiceAddressElementRef.nativeElement, 'change', () => {
              this.ngZone.run(() => {
                this.invoiceAddress = null;
              });
            });
          });

        } else {
          this.houseForm.removeControl('invoiceAddress');
        }
      });

    if (this.userData) {
      setTimeout(() => {
        this.patchValue();
      });
    }
  }

  patchValue() {
    if (this.userData.house) {
      const data = {
        atHome: this.userData.house.atHome,
        name: this.userData.house.name,
        shippingAddress: {
          lastName: this.userData.house.shippingAddress.lastName,
          firstName: this.userData.house.shippingAddress.firstName,
          company: this.userData.house.shippingAddress.company,
          address: this.userData.house.shippingAddress.formattedAddress,
          phoneNumber: this.userData.house.shippingAddress.phoneNumber,
          pickupId: this.userData.house.shippingAddress.pickupId,
        },
        differentInvoiceAddress: this.userData.house.shippingAddressId !== this.userData.house.invoiceAddressId,
        invoiceAddress: {
          lastName: this.userData.house.invoiceAddress.lastName,
          firstName: this.userData.house.invoiceAddress.firstName,
          company: this.userData.house.invoiceAddress.company,
          address: this.userData.house.invoiceAddress.formattedAddress,
        }
      };
      if (!data.differentInvoiceAddress) {
        delete data.invoiceAddress;
      } else {
        this.houseForm.addControl('invoiceAddress', this.fb.group({
          lastName: ['', Validators.required],
          firstName: ['', Validators.required],
          company: [''],
          address: ['', Validators.required],
        }));
      }
      this.houseForm.patchValue(data);
    } else {
      this.houseForm.patchValue({
        shippingAddress: {
          lastName: this.userData.lastName,
          firstName: this.userData.firstName,
          company: this.userData.company,
          phoneNumber: this.userData.phoneNumber,
        },
        differentInvoiceAddress: false,
      });
    }
  }

  initGMaps() {
    this.mapsAPILoader.load().then(() => {
      if (this.userData.house) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({'address': this.userData.house.shippingAddress.formattedAddress}, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            this.ngZone.run(() => {
              // @ts-ignore
              this.shippingAddress = results[0];
            });
          }
        });
        geocoder.geocode({'address': this.userData.house.invoiceAddress.formattedAddress}, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            this.ngZone.run(() => {
              // @ts-ignore
              this.invoiceAddress = results[0];
            });
          }
        });
      }

      const autocompleteShipping = new google.maps.places.Autocomplete(this.shippingAddressElementRef.nativeElement, {
        types: ['address'],
      });

      autocompleteShipping.addListener('place_changed', () => {
        this.ngZone.run(() => {
          this.shippingAddress = autocompleteShipping.getPlace();
        });
      });
      this.renderer.listen(this.shippingAddressElementRef.nativeElement, 'change', () => {
        this.ngZone.run(() => {
          this.shippingAddress = null;
        });
      });
    });
  }

  get shippingAddressForm(): FormGroup {
    return this.houseForm.get('shippingAddress') as FormGroup;
  }

  get invoiceAddressForm(): FormGroup {
    return this.houseForm.get('invoiceAddress') as FormGroup;
  }

  onSubmit() {
    const formValue = this.houseForm.value;
    const number = PhoneNumberUtil.getInstance().parseAndKeepRawInput(formValue.shippingAddress.phoneNumber);
    const result: { atHome: boolean, name: string, shippingAddress: { pickupId: string } & IAddressContent, invoiceAddress?: IAddressContent } = {
      atHome: formValue.atHome,
      name: formValue.name,
      shippingAddress: {
        firstName: formValue.shippingAddress.firstName,
        lastName: formValue.shippingAddress.lastName,
        company: formValue.shippingAddress.company,
        streetNumber: this.shippingAddress === null ? this.userData.house.shippingAddress.streetNumber : getAddressField(this.shippingAddress, AddressField.STREETNUMBER),
        route: this.shippingAddress === null ? this.userData.house.shippingAddress.route : getAddressField(this.shippingAddress, AddressField.ROUTE),
        locality: this.shippingAddress === null ? this.userData.house.shippingAddress.locality : getAddressField(this.shippingAddress, AddressField.LOCALITY),
        country: this.shippingAddress === null ? this.userData.house.shippingAddress.country : getAddressField(this.shippingAddress, AddressField.COUNTRY),
        postalCode: this.shippingAddress === null ? this.userData.house.shippingAddress.postalCode : getAddressField(this.shippingAddress, AddressField.POSTALCODE),
        formattedAddress: this.shippingAddress === null ? this.userData.house.shippingAddress.formattedAddress : this.shippingAddress.formatted_address,
        lat: this.shippingAddress === null ? this.userData.house.shippingAddress.lat : this.shippingAddress.geometry.location.lat(),
        lng: this.shippingAddress === null ? this.userData.house.shippingAddress.lng : this.shippingAddress.geometry.location.lng(),
        phoneNumber: PhoneNumberUtil.getInstance().format(number, PhoneNumberFormat.INTERNATIONAL),
        additional: '',
        pickupId: formValue.shippingAddress.pickupId,
      },
      invoiceAddress: formValue.differentInvoiceAddress ? {
        firstName: formValue.invoiceAddress.firstName,
        lastName: formValue.invoiceAddress.lastName,
        company: formValue.invoiceAddress.company,
        streetNumber: this.invoiceAddress === null ? this.userData.house.shippingAddress.streetNumber : getAddressField(this.invoiceAddress, AddressField.STREETNUMBER),
        route: this.invoiceAddress === null ? this.userData.house.shippingAddress.route : getAddressField(this.invoiceAddress, AddressField.ROUTE),
        locality: this.invoiceAddress === null ? this.userData.house.shippingAddress.locality : getAddressField(this.invoiceAddress, AddressField.LOCALITY),
        country: this.invoiceAddress === null ? this.userData.house.shippingAddress.country : getAddressField(this.invoiceAddress, AddressField.COUNTRY),
        postalCode: this.invoiceAddress === null ? this.userData.house.shippingAddress.postalCode : getAddressField(this.invoiceAddress, AddressField.POSTALCODE),
        formattedAddress: this.invoiceAddress === null ? this.userData.house.shippingAddress.formattedAddress : this.invoiceAddress.formatted_address,
        lat: this.invoiceAddress === null ? this.userData.house.shippingAddress.lat : this.invoiceAddress.geometry.location.lat(),
        lng: this.invoiceAddress === null ? this.userData.house.shippingAddress.lng : this.invoiceAddress.geometry.location.lng(),
        phoneNumber: PhoneNumberUtil.getInstance().format(number, PhoneNumberFormat.INTERNATIONAL),
        additional: '',
      } : null,
    };

    this.activeModal.close(result);
  }
}
